import React, { useMemo } from 'react';
import { Icon } from '@iconify/react';
import { ExceptionServiceState } from '../../../types/zitoneoServicesTypes';
import { formatDate } from '../../../../../utils/formatDates';
import { useZitoneoPreview } from '../../../contexts/ZitoneoContentContext';
import useTranslate from '../../../../../customHooks/useTranslate';
import Tag from '../../../../../muiComponents/Tag';

interface ServiceExceptionProps {
    exception: ExceptionServiceState;
}

const ServiceException = ({ exception }: ServiceExceptionProps) => {
    const { visibleLanguage } = useZitoneoPreview();
    const translate = useTranslate(undefined, visibleLanguage?.Code);

    const statusLabel = useMemo(() => {
        const now = new Date();
        const today = new Date(
            now.getFullYear(),
            now.getMonth(),
            now.getDate()
        );

        const parseDate = (value: Date | string): Date | null => {
            if (!value) {
                return null;
            }
            const parsed = new Date(value);
            if (Number.isNaN(parsed.getTime())) {
                return null;
            }
            // Guard against sentinel values used as "no end date".
            if (parsed.getFullYear() <= 1900) {
                return null;
            }
            return new Date(
                parsed.getFullYear(),
                parsed.getMonth(),
                parsed.getDate()
            );
        };

        const isInEffect = exception.Dates?.some(date => {
            const from = parseDate(date.FromDate);
            const to = parseDate(date.ToDate);
            return from && from <= today && (!to || to >= today);
        });

        if (isInEffect) return translate('in_effect');

        const isPast = exception.Dates?.every(date => {
            const to = parseDate(date.ToDate);
            return to !== null && to < today;
        });

        if (isPast) return translate('past');

        return translate('scheduled');
    }, [exception.Dates, translate]);

    return (
        <article className='service-content-item__errata'>
            <div className='service-content-item__errata-header'>
                <div className='service-content-item__errata-header__main'>
                    <Icon
                        icon='material-symbols:info-outline'
                        className='service-content-item__errata-icon'
                    />
                    <h6 className='service-content-item__errata-title'>
                        {translate('errata')} - {statusLabel}
                    </h6>
                    {exception.IdServiceExceptionLevelType ? (
                        <Tag
                            variant='contrast'
                            color='orange'
                            text={'critical'}
                        />
                    ) : (
                        ''
                    )}
                </div>

                {exception.Dates &&
                    exception.Dates.length > 0 &&
                    exception.Dates.map(date => (
                        <p
                            className='service-content-item__errata-date'
                            key={date.Id}
                        >
                            {formatDate(date.FromDate, visibleLanguage?.Code)} -{' '}
                            {formatDate(date.ToDate, visibleLanguage?.Code)}
                        </p>
                    ))}
            </div>

            <div className='service-content-item__errata-body'>
                {exception.Description || ''}
            </div>
        </article>
    );
};

export default ServiceException;
